import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Row,
  Col,
  Typography,
  Space,
  message,
  Switch
} from 'antd';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';
import ProductStatusFilter from 'app/shared/components/ProductStatusFilter';
import {
  ProductStatusFilterValue,
  matchesProductStatusFilter,
} from 'app/shared/components/productStatusFilterUtils';
import { PlusOutlined } from '@ant-design/icons';
import { getSelectedDivisionId } from 'app/shared/division/selectedDivisionStorage';
import {
  Product
} from 'app/shared/model/ProductBase';
import {
  fetchIntensityList,
  createOrUpdateIntensity,
  createOrUpdateSchoolConfig
} from './actions';
import {
  getIntensityList,
  getIntensityLoading
} from './selectors';
import { CourseIntensityDto, SchoolConfig, SchoolOverride } from 'app/shared/model';
import TableActionButtons from 'app/shared/components/Table/TableActionButtons';
import ManagerSearchByName from 'app/shared/components/ManagerSearchByName';
import { getAllSchools } from 'app/shared/api/getSchools';
import { School } from 'app/shared/model/School';
import { PublishedEventType, StatusIcon, useSignalREvent } from 'app/helpers/signalRHelper';
import DateHelper from 'app/helpers/dateHelper';

const { Title } = Typography;

function overridesToConfigs(overrides: SchoolOverride[] | undefined, intensityId: number): SchoolConfig[] {
  if (!overrides || overrides.length === 0) return [];
  return overrides.map(override => ({
    id: override.schoolId,
    schoolIds: [override.schoolId],
    schoolName: override.schoolName,
    lessons: override.numberOfLessons,
    hours: override.numberOfHours,
    cores: override.numberOfCores,
    electives: override.numberOfElectives,
    length: override.lessonLength,
    intensityId
  }));
}

const CourseIntensityPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const intensityList = useSelector(getIntensityList);
  const intensityLoading = useSelector(getIntensityLoading);
  const [showFilters, setShowFilters] = useState(false);
  const [searchByName, setSearchByName] = useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedIntensity, setSelectedIntensity] = useState<CourseIntensityDto>();
  const [intModalVisible, setIntModalVisible]       = useState(false);
  const [cfgModalVisible, setCfgModalVisible]       = useState(false);
  const [intForm] = Form.useForm();
  const [cfgForm] = Form.useForm();
  const [activeStatusFilter, setActiveStatusFilter] = useState<ProductStatusFilterValue>('all');
  const [filteredIntensities, setFilteredIntensities] = useState<CourseIntensityDto[]>([]);
  const [allSchools, setAllSchools] = useState<School[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [expandAll, setExpandAll] = useState(false);
  const [editingIntensityForModal, setEditingIntensityForModal] = useState<CourseIntensityDto | undefined>(undefined);
  const editingIntensityIdRef = useRef<number | null>(null);

  const refreshGridEvent = async () => {
    dispatch(fetchIntensityList());
  }

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent([PublishedEventType.CourseIntensityEventPublished, PublishedEventType.CourseIntensitySchoolOverrideEventPublished, PublishedEventType.CourseIntensitySchoolOverrideEventPublished], refreshGridEvent);

  useEffect(() => {
    dispatch(fetchIntensityList());
  }, [dispatch]);

  useEffect(() => {
    getAllSchools()
      .then(schools => {
        const uniqueSchools = schools.filter((school, index, self) =>
          index === self.findIndex(s => s.code === school.code)
        );
        setAllSchools(uniqueSchools);
      })
      .catch(err => {
        message.error("Failed to load schools");
        console.error(err);
      });
  }, []);



  const openEditModal = (record: CourseIntensityDto) => {
    const id = Number(record.id ?? (record as any).Id ?? 0);
    editingIntensityIdRef.current = id > 0 ? id : null;
    setEditingIntensityForModal(record);
    intForm.setFieldsValue(record);
    setIntModalVisible(true);
  };

  const divisionId = getSelectedDivisionId();
  const intensitiesForValidation = divisionId > 0
    ? intensityList.filter(i => (i.divisionId ?? 0) === 0 || i.divisionId === divisionId)
    : intensityList;

  const intensityColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: CourseIntensityDto) => (
        <a
          href="#0"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openEditModal(record);
          }}
        >
          {name}
        </a>
      )
    },
    {
      title: 'Last Modified Date',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      render: (val: Date) => DateHelper.ToDateString(val)
    },
    {
      title: 'Last Modified By',
      dataIndex: 'lastModifiedBy',
      key: 'lastModifiedBy',
      render: (v: string) => v ?? '-'
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      sorter: (a: CourseIntensityDto, b: CourseIntensityDto) => {
        const activeA = a.active ? 1 : 0;
        const activeB = b.active ? 1 : 0;
        return activeB - activeA;
      },
      render: (v: boolean) => v ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>
    }
  ];

  const configColumns = [
    {
      title: 'School Name',
      dataIndex: 'schoolName',
      key: 'schoolName',
      render: (schoolName: string, record: SchoolConfig) => {
        if (schoolName) {
          return schoolName;
        }
        if (record.schoolIds && record.schoolIds.length > 0) {
          const school = allSchools.find(s => 
            s.id === record.schoolIds[0] || 
            s.code === String(record.schoolIds[0]) ||
            String(s.id) === String(record.schoolIds[0])
          );
          return school ? school.name : String(record.schoolIds[0]);
        }
        return '-';
      }
    },
    { title: '# Lessons',     dataIndex: 'lessons',    key: 'lessons'    },
    { title: '# Hours',       dataIndex: 'hours',      key: 'hours'      },
    { title: '# Cores',       dataIndex: 'cores',      key: 'cores'      },
    { title: '# Electives',   dataIndex: 'electives',  key: 'electives'  },
    { title: 'Lesson Length (min)', dataIndex: 'length',     key: 'length'     },
  ];

  const onAddIntensity = () => {
    editingIntensityIdRef.current = null;
    setEditingIntensityForModal(undefined);
    intForm.resetFields();
    setIntModalVisible(true);
  };
  const submitIntensity = async () => {
    try {
      const vals = await intForm.validateFields();
      const id = editingIntensityIdRef.current;
      const intensityName = vals.name.trim();
      const duplicateIntensity = intensitiesForValidation.find(
        intensity => 
          intensity.name.trim().toLowerCase() === intensityName.toLowerCase() &&
          (id == null || id === 0 || Number(intensity.id) !== id)
      );
      
      if (duplicateIntensity) {
        message.error(`An intensity with the name "${intensityName}" already exists. Please use a different name.`);
        return;
      }
      
      const payload = {
        name: intensityName,
        active: vals.active,
        ...(id != null && id > 0 ? { id } : {}),
      };
      await dispatch(createOrUpdateIntensity(payload));
      setIntModalVisible(false);
      setEditingIntensityForModal(undefined);
      dispatch(fetchIntensityList());
      message.success(id != null && id > 0 ? 'Intensity updated successfully' : 'Intensity added successfully');
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Failed to save intensity');
    }
  };

  const onAddConfig = (record: CourseIntensityDto) => {
    setSelectedIntensity(record);
    cfgForm.resetFields();
    setCfgModalVisible(true);
  };
  const onEditConfig = (record: SchoolConfig, intensityRecord: CourseIntensityDto) => {
    setSelectedIntensity(intensityRecord);
    const schoolCodes = record.schoolIds?.map(id => {
      const school = allSchools.find(s => 
        s.id === id || 
        String(s.id) === String(id) ||
        s.code === String(id)
      );
      return school ? school.code : String(id);
    }) || [];
    
    cfgForm.setFieldsValue({
      ...record,
      schoolIds: schoolCodes
    });
    setCfgModalVisible(true);
  };
  const submitConfig = async () => {
    if (!selectedIntensity) return;
    const vals = await cfgForm.validateFields();
    await dispatch(createOrUpdateSchoolConfig({
      intensityId: selectedIntensity.id,
      ...vals
    }));
    setCfgModalVisible(false);
    dispatch(fetchIntensityList());
  };

  const searchedData = useMemo(() => {
    const needle = (searchByName || '').trim().toLowerCase();
    const base = Array.isArray(intensityList) ? intensityList : [];
    if (!needle) return base;
    return base.filter((x) => String(x?.name ?? '').toLowerCase().includes(needle));
  }, [intensityList, searchByName]);
  const dataSource = useMemo(
    () =>
      searchedData.filter((item: CourseIntensityDto) =>
        matchesProductStatusFilter(item.active, activeStatusFilter)
      ),
    [searchedData, activeStatusFilter]
  );
  const dataSourceIds = useMemo(
    () => dataSource.map((r: CourseIntensityDto) => r.id) as React.Key[],
    [dataSource]
  );
  const arraysEqual = (a: React.Key[], b: React.Key[]) =>
    a.length === b.length && a.every((v, i) => String(v) === String(b[i]));

  useEffect(() => {
    if (!expandAll) return;
    setExpandedRowKeys((prev) => (arraysEqual(prev, dataSourceIds) ? prev : dataSourceIds));
    if (dataSourceIds.length > 0) {
      const firstId = dataSourceIds[0];
      const intensity = dataSource.find((i: CourseIntensityDto) => Number(i.id) === Number(firstId));
      if (intensity) setSelectedIntensity(intensity);
    }
  }, [expandAll, dataSourceIds, dataSource]);

  const onExpandAllChange = (checked: boolean) => {
    setExpandAll(checked);
    if (!checked) {
      setExpandedRowKeys([]);
      setSelectedIntensity(undefined);
    }
  };

  return (
    <>
    <Title level={3} style={{ margin: 0, marginBottom: 16 }}>Course Intensity Manager</Title>
    <Space
        style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }}
      >
      <Space>
          <ManagerSearchByName
            value={searchByName}
            onChange={(v) => {
              setSearchByName(v);
              setCurrentPage(1);
            }}
            disabled={showFilters}
          />
        <ProductStatusFilter
          value={activeStatusFilter}
          onChange={(value) => {
            setActiveStatusFilter(value);
            setCurrentPage(1);
          }}
        />
        <Switch
          checked={expandAll}
          onChange={onExpandAllChange}
          checkedChildren="Collapse All"
          unCheckedChildren="Expand All"
        />
      </Space>
    <Space>
      <StatusIcon status={status} lastEventReceived={lastEventReceived} unpublishedChanges={Array.isArray(filteredIntensities) && filteredIntensities.some(x => x?.entityUpdate === true)} onManualRefresh={refreshGridEvent}/>
      <TableActionButtons
        showFilters={false}
       dataList={intensityList as any as Product[]}
        onShowFiltersClick={() => {}}
        canFilter={false}
        canExport
        canEdit={false}
        canAdd={false}
        csvFileName={`${new Date().toISOString().split("T")[0]}_CourseIntensities_Export`}
      />
      <Button type="primary" onClick={onAddIntensity} icon={<PlusOutlined />}>
        Add New
      </Button>
    </Space>
  </Space>

      {/* Intensity list */}
      {intensityLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table<CourseIntensityDto>
          rowKey="id"
          dataSource={dataSource}
          columns={intensityColumns}
          pagination={false}
          expandable={{
            expandedRowRender: (record) => {
              const configs = overridesToConfigs(record.schoolOverrides, Number(record.id));
              return (
                <div style={{ padding: '16px 0' }}>
                  <Row justify="space-between" style={{ marginBottom: 16 }}>
                    <Col>
                      <strong>Configs for:</strong> {record.name}
                    </Col>
                    <Col>
                      <Button type="dashed" onClick={() => onAddConfig(record)} icon={<PlusOutlined />}>
                        Add Details
                      </Button>
                    </Col>
                  </Row>
                  <Table<SchoolConfig>
                    rowKey="id"
                    dataSource={configs}
                    columns={configColumns}
                    pagination={false}
                    size="small"
                    onRow={rowRecord => ({
                      onClick: () => onEditConfig(rowRecord, record)
                    })}
                  />
                </div>
              );
            },
            expandedRowKeys,
            onExpandedRowsChange: (keys) => {
              const newKeys = keys as React.Key[];
              const allExpanded = newKeys.length === dataSource.length && dataSource.length > 0;
              if (expandAll !== allExpanded) setExpandAll(allExpanded);
              setExpandedRowKeys(newKeys);

              if (newKeys.length > 0) {
                const expandedId = newKeys[newKeys.length - 1];
                const intensity = dataSource.find((i: CourseIntensityDto) => Number(i.id) === Number(expandedId));
                if (intensity) setSelectedIntensity(intensity);
                else setSelectedIntensity(undefined);
              } else {
                setSelectedIntensity(undefined);
              }
            },
            expandRowByClick: false,
            rowExpandable: () => true,
          }}
          rowClassName={record =>
            record.id === selectedIntensity?.id ? 'ant-table-row-selected' : ''
          }
        />
      )}

      {/* Intensity Modal */}
      <Modal
        title={editingIntensityForModal ? 'Edit Intensity' : 'Add New Intensity'}
        open={intModalVisible}
        onOk={submitIntensity}
        onCancel={() => {
          setIntModalVisible(false);
          setEditingIntensityForModal(undefined);
        }}
      >
        <Form form={intForm} layout="vertical" initialValues={{ active: true }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Please enter a name' },
              {
                validator: async (_, value) => {
                  if (!value || !value.trim()) {
                    return Promise.resolve();
                  }
                  const intensityName = value.trim();
                  const id = editingIntensityIdRef.current;
                  const duplicateIntensity = intensitiesForValidation.find(
                    intensity => 
                      intensity.name.trim().toLowerCase() === intensityName.toLowerCase() &&
                      (id == null || id === 0 || Number(intensity.id) !== id)
                  );
                  
                  if (duplicateIntensity) {
                    return Promise.reject(new Error(`An intensity with the name "${intensityName}" already exists. Please use a different name.`));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input />
          </Form.Item>
          <ActiveStatusFormItem name="active" />
        </Form>
      </Modal>

      {/* School Config Modal */}
      <Modal
        title="School Configuration"
        open={cfgModalVisible}
        onOk={submitConfig}
        onCancel={() => setCfgModalVisible(false)}
      >
        <Form form={cfgForm} layout="vertical">
          <Form.Item
            name="startingYear"
            label="Starting Year"
            rules={[{ required: true, message: 'Please enter starting year' }]}
            initialValue={new Date().getFullYear()}
          >
            <InputNumber min={2000} max={2100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="schoolIds"
            label="School(s)"
            rules={[{ required: true, message: 'Select at least one school' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select schools"
              options={allSchools.map((school, index) => ({
                value: school.code,
                label: `${school.name} (${school.centreCode})`,
                key: school.code || `school-${index}`,
              }))}
              showSearch={{
                filterOption: (input, option) => {
                  const label = String(option?.label ?? '').toLowerCase();
                  const value = String(option?.value ?? '').toLowerCase();
                  const searchText = input.toLowerCase();
                  return label.includes(searchText) || value.includes(searchText);
                },
              }}
            />
          </Form.Item>
          <Form.Item name="lessons" label="Number of Lessons" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="hours" label="Number of Hours" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="cores" label="Core Lessons Per Week" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="electives" label="Elective Lessons Per Week" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="length" label="Lesson Length (minutes)" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CourseIntensityPage;